import { useEffect, useState } from "react";
import axios from "axios";
import { Repeat } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * RecurringIncomePanel — Feb 6, 2026.
 *
 * Owner directive: "Growth = passive outcome of good teaching
 * (membership income), not a separate system."
 *
 * Lists pupils on Membership and shows the £2/month total. No
 * celebratory UI, no "rewards" language, no referral-stack chrome.
 * Empty state is honest: "No pupils on Membership yet."
 *
 * The backend endpoint `GET /api/instructor/recurring-income` is
 * the single source of truth. If it 404s or errors, the panel
 * silently hides — never shows an error toast.
 */
export default function RecurringIncomePanel() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/instructor/recurring-income`, { withCredentials: true });
        if (!cancelled) setData(r.data || { items: [], total_monthly_pence: 0 });
      } catch {
        if (!cancelled) setData({ items: [], total_monthly_pence: 0 });
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded) return null;

  const items = data?.items || [];
  const totalMonthly = (data?.total_monthly_pence || 0) / 100;

  return (
    <div
      data-testid="recurring-income-panel"
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
          <Repeat className="w-4 h-4 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white leading-tight">Recurring Income</p>
          <p className="text-[11px] text-slate-500 mt-0.5">Pupils on Membership · £2/month each</p>
        </div>
      </div>

      {items.length === 0 ? (
        <p
          data-testid="recurring-income-empty"
          className="text-xs text-slate-500 dark:text-slate-400 leading-snug"
        >
          No pupils on Membership yet. When a pupil chooses Membership, £2/month appears here automatically.
        </p>
      ) : (
        <>
          <ul data-testid="recurring-income-list" className="divide-y divide-slate-100 dark:divide-slate-800 -mx-1">
            {items.map((p) => (
              <li
                key={p.pupil_id}
                data-testid={`recurring-income-row-${p.pupil_id}`}
                className="px-1 py-2 flex items-center gap-3"
              >
                <span className="flex-1 min-w-0 text-sm text-slate-900 dark:text-white truncate">{p.pupil_name}</span>
                <span className="text-xs text-slate-500">Membership</span>
                <span className="text-sm font-bold text-emerald-600 tabular-nums">£{((p.monthly_pence || 0) / 100).toFixed(2)}/month</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider font-bold text-slate-500">Total recurring</span>
            <span data-testid="recurring-income-total" className="text-base font-extrabold text-emerald-600 tabular-nums">£{totalMonthly.toFixed(2)}/month</span>
          </div>
        </>
      )}
    </div>
  );
}
